import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import FlashOnIcon from "@mui/icons-material/FlashOn";

import {
  fetchSmartRoutingSuggestions,
  triggerSmartRoutingRecalc,
  dispatchSmartRoutingRoute,
  selectDriver,
  setActiveRoute,
  moveOrderToRoute,
  selectSmartRoutingRoutes,
  selectSmartRoutingSoloOrders,
  selectSmartRoutingCalculation,
} from "../../store/actions/smartRoutingSlice";
import { setMapViewState } from "../../store/actions/dashboardSlice";
import { getDrivers } from "../../api/driver";
import { dispatchSnackBarMessage } from "../../utils/snackbar";

const POLL_INTERVAL_MS = 30000;

/* ────── animations ────── */

const listVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

/* ────── main ────── */

export default function DashboardSmartRoutes() {
  const theme = useTheme();
  const dispatch = useDispatch();

  const calculation = useSelector(selectSmartRoutingCalculation);
  const routes = useSelector(selectSmartRoutingRoutes);
  const soloOrders = useSelector(selectSmartRoutingSoloOrders);
  const loading = useSelector((s) => s.smartRouting.loading);
  const recalculating = useSelector((s) => s.smartRouting.recalculating);
  const dispatchingClusterId = useSelector(
    (s) => s.smartRouting.dispatchingClusterId
  );
  const driverSelections = useSelector(
    (s) => s.smartRouting.driverSelections
  );
  const activeRouteIdx = useSelector((s) => s.smartRouting.activeRouteIdx);
  const [drivers, setDrivers] = useState([]);

  const routeColors = useMemo(
    () => [
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.error.main,
    ],
    [theme]
  );

  useEffect(() => {
    // Auto-hide the dashboard map when entering Rotas — the route cards
    // need the full pane width and the map preview is still reachable via
    // the eye icon in the tab bar (hover for preview, click to pin open).
    dispatch(setMapViewState("hidden"));

    dispatch(fetchSmartRoutingSuggestions());
    getDrivers()
      .then(({ data }) => setDrivers(data?.results || data || []))
      .catch(() => setDrivers([]));
    const id = setInterval(
      () => dispatch(fetchSmartRoutingSuggestions()),
      POLL_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [dispatch]);

  const onRecalc = () => dispatch(triggerSmartRoutingRecalc());

  const onDispatch = async (route) => {
    const driverId = driverSelections[route.cluster_id];
    if (!driverId) return;
    const result = await dispatch(
      dispatchSmartRoutingRoute({
        calculation_id: calculation?.calculation_id,
        cluster_id: route.cluster_id,
        driver_id: driverId,
        order_ids: route.orders.map((o) => o.order_id),
      })
    );
    if (result.error) {
      dispatchSnackBarMessage(
        `smart-routing-dispatch-${Date.now()}`,
        result.payload?.error || "Erro ao despachar rota",
        "error"
      );
    } else {
      dispatchSnackBarMessage(
        `smart-routing-dispatch-${Date.now()}`,
        `Rota despachada · ${route.orders.length} entregas`,
        "success"
      );
    }
  };

  const onDispatchSolo = async (order, driverId) => {
    if (!driverId) return;
    const result = await dispatch(
      dispatchSmartRoutingRoute({
        calculation_id: calculation?.calculation_id,
        cluster_id: `solo-${order.order_id}`,
        driver_id: driverId,
        order_ids: [order.order_id],
      })
    );
    if (result.error) {
      dispatchSnackBarMessage(
        `smart-routing-dispatch-${Date.now()}`,
        result.payload?.error || "Erro ao despachar",
        "error"
      );
    } else {
      dispatchSnackBarMessage(
        `smart-routing-dispatch-${Date.now()}`,
        "Pedido despachado sozinho",
        "success"
      );
    }
  };

  const onDragStart = (e, orderId) => {
    e.dataTransfer.setData("text/plain", orderId);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = (e, targetClusterId) => {
    e.preventDefault();
    const orderId = e.dataTransfer.getData("text/plain");
    if (orderId)
      dispatch(moveOrderToRoute({ orderId, targetClusterId }));
  };

  if (loading && !calculation) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isEmpty = routes.length === 0 && soloOrders.length === 0;

  return (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      <PaneHeader
        calculation={calculation}
        recalculating={recalculating}
        onRecalc={onRecalc}
        theme={theme}
        routesCount={routes.length}
        soloCount={soloOrders.length}
      />

      {isEmpty ? (
        <EmptyState theme={theme} />
      ) : (
        <Box sx={{ p: 1 }}>
          {routes.length > 0 && (
            <>
              <SectionLabel
                icon={<AltRouteIcon sx={{ fontSize: 14 }} />}
                label="Rotas sugeridas"
                count={routes.length}
                theme={theme}
              />
              <Grid
                container
                spacing={2}
                justifyContent="center"
                component={motion.div}
                variants={listVariants}
                initial="hidden"
                animate="show"
              >
                <AnimatePresence>
                  {routes.map((route, i) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={route.cluster_id}
                      component={motion.div}
                      variants={itemVariants}
                      layout
                    >
                      <RouteCard
                        route={route}
                        index={i}
                        color={routeColors[i % routeColors.length]}
                        active={i === activeRouteIdx}
                        onActivate={() => dispatch(setActiveRoute(i))}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => onDrop(e, route.cluster_id)}
                        onOrderDragStart={onDragStart}
                        drivers={drivers}
                        selectedDriverId={
                          driverSelections[route.cluster_id] || ""
                        }
                        onDriverChange={(id) =>
                          dispatch(
                            selectDriver({
                              clusterId: route.cluster_id,
                              driverId: id,
                            })
                          )
                        }
                        onDispatch={() => onDispatch(route)}
                        dispatching={
                          dispatchingClusterId === route.cluster_id
                        }
                        theme={theme}
                      />
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>
            </>
          )}

          {soloOrders.length > 0 && (
            <Box sx={{ mt: routes.length > 0 ? 3 : 0 }}>
              <SectionLabel
                icon={<HourglassEmptyIcon sx={{ fontSize: 14 }} />}
                label="Aguardando companheiro · despachar sozinho"
                count={soloOrders.length}
                theme={theme}
              />
              <Box
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, "solo")}
              >
                <Grid
                  container
                  spacing={2}
                  justifyContent="center"
                  component={motion.div}
                  variants={listVariants}
                  initial="hidden"
                  animate="show"
                >
                  {soloOrders.map((o) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={o.order_id}
                      component={motion.div}
                      variants={itemVariants}
                      layout
                    >
                      <SoloCard
                        order={o}
                        drivers={drivers}
                        onDragStart={(e) => onDragStart(e, o.order_id)}
                        onDispatch={(driverId) => onDispatchSolo(o, driverId)}
                        dispatching={
                          dispatchingClusterId === `solo-${o.order_id}`
                        }
                        theme={theme}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

/* ────── header ────── */

function PaneHeader({
  calculation,
  recalculating,
  onRecalc,
  routesCount,
  soloCount,
  theme,
}) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Avatar
          sx={{
            bgcolor: alpha(theme.palette.warning.main, 0.12),
            color: theme.palette.warning.main,
            width: 32,
            height: 32,
          }}
        >
          <ExploreOutlinedIcon fontSize="small" />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            lineHeight={1.2}
            noWrap
          >
            Sugestões de roteirização
          </Typography>
          {calculation?.created_at && (
            <Typography variant="caption" color="text.secondary">
              Atualizado às{" "}
              <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {new Date(calculation.created_at).toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Box>
              {" · "}
              {routesCount} {routesCount === 1 ? "rota" : "rotas"} ·{" "}
              {soloCount} {soloCount === 1 ? "avulso" : "avulsos"}
            </Typography>
          )}
        </Box>
      </Stack>
      <Tooltip title="Recalcular">
        <span>
          <IconButton
            size="small"
            onClick={onRecalc}
            disabled={recalculating}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": { color: theme.palette.warning.main },
            }}
          >
            <RefreshIcon
              sx={{
                fontSize: 20,
                animation: recalculating ? "spin 1s linear infinite" : "none",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

/* ────── section label ────── */

function SectionLabel({ icon, label, count, theme }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ px: 1, mb: 1.25, color: theme.palette.text.secondary }}
    >
      {icon}
      <Typography
        variant="caption"
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 700,
          fontSize: "0.65rem",
        }}
      >
        {label}
      </Typography>
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{
            height: 16,
            fontSize: "0.65rem",
            fontWeight: 600,
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            color: theme.palette.text.secondary,
          }}
        />
      )}
    </Stack>
  );
}

/* ────── empty state ────── */

function EmptyState({ theme }) {
  return (
    <Box sx={{ p: 4 }}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: alpha(theme.palette.grey[300], 0.8),
          backgroundColor: theme.palette.background.paper,
          textAlign: "center",
          py: 5,
          px: 3,
        }}
      >
        <Avatar
          sx={{
            bgcolor: alpha(theme.palette.grey[500], 0.1),
            color: theme.palette.grey[500],
            width: 48,
            height: 48,
            mx: "auto",
            mb: 1.5,
          }}
        >
          <ExploreOutlinedIcon />
        </Avatar>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          Nada para roteirizar agora
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 360, mx: "auto", lineHeight: 1.5 }}
        >
          Quando pedidos forem marcados como embalados, sugestões de rotas
          aparecem aqui.
        </Typography>
      </Card>
    </Box>
  );
}

/* ────── route card ────── */

function RouteCard({
  route,
  index,
  color,
  active,
  onActivate,
  onDragOver,
  onDrop,
  onOrderDragStart,
  drivers,
  selectedDriverId,
  onDriverChange,
  onDispatch,
  dispatching,
  theme,
}) {
  const lateCount = (route.orders || []).filter(
    (o) => getDeadlineState(o.delivery_expected_ts).state === "late"
  ).length;
  const almostLateCount = (route.orders || []).filter(
    (o) => getDeadlineState(o.delivery_expected_ts).state === "almostLate"
  ).length;

  return (
    <Card
      onClick={onActivate}
      onDragOver={onDragOver}
      onDrop={onDrop}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        cursor: "pointer",
        border: `1px solid ${
          active ? color : alpha(theme.palette.grey[300], 0.5)
        }`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}, 0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: color,
          boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.1)}, 0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2, pb: 1 }}>
        {/* Header: route badge + count + deadline */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.12),
              color: color,
              width: 36,
              height: 36,
              fontSize: "0.95rem",
              fontWeight: 700,
            }}
          >
            {String.fromCharCode(65 + index)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ mb: 0.25 }}
              flexWrap="wrap"
              rowGap={0.5}
            >
              <Typography variant="subtitle1" fontWeight={600} lineHeight={1}>
                Rota {String.fromCharCode(65 + index)}
              </Typography>
              <DispatchSequenceChip index={index} theme={theme} />
              {lateCount > 0 && (
                <Chip
                  label={`${lateCount} ${lateCount === 1 ? "atrasado" : "atrasados"}`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    bgcolor: alpha(theme.palette.error.main, 0.14),
                    color: theme.palette.error.main,
                    borderRadius: 1,
                  }}
                />
              )}
              {lateCount === 0 && almostLateCount > 0 && (
                <Chip
                  label={`${almostLateCount} quase atrasado${almostLateCount === 1 ? "" : "s"}`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    bgcolor: alpha(theme.palette.warning.main, 0.14),
                    color: theme.palette.warning.main,
                    borderRadius: 1,
                  }}
                />
              )}
              {route.isOverridden && (
                <Chip
                  label="editada"
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.info.main,
                  }}
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontFamily: "monospace",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {route.orders.length}{" "}
              {route.orders.length === 1 ? "entrega" : "entregas"}
              {Number.isFinite(route.total_distance_km) && (
                <>
                  {" · "}
                  {route.total_distance_km.toFixed(1)} km
                </>
              )}
              {Number.isFinite(route.total_duration_minutes) && (
                <>
                  {" · "}
                  {Math.round(route.total_duration_minutes)} min
                </>
              )}
            </Typography>
          </Box>
          {route.earliest_deadline_ts && (
            <DeadlineChip deadlineTs={route.earliest_deadline_ts} theme={theme} />
          )}
        </Stack>

        {Number.isFinite(route.bearing_spread) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mb: 1.5,
              fontWeight: 500,
            }}
          >
            Leque {Math.round(route.bearing_spread)}°
            {route.detour_ratio
              ? ` · desvio ${route.detour_ratio.toFixed(2)}×`
              : ""}
          </Typography>
        )}

        <Divider sx={{ my: 1.5, opacity: 0.6 }} />

        {/* Body: order chips */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{
              mb: 0.75,
              display: "block",
              textTransform: "uppercase",
              fontSize: "0.65rem",
            }}
          >
            Pedidos
          </Typography>
          <Stack spacing={0.5}>
            {route.orders.map((o, i) => (
              <OrderChip
                key={o.order_id}
                order={o}
                index={i + 1}
                color={color}
                onDragStart={(e) => onOrderDragStart(e, o.order_id)}
                theme={theme}
              />
            ))}
          </Stack>
        </Box>
      </CardContent>

      <CardActions
        onClick={(e) => e.stopPropagation()}
        sx={{
          px: 2,
          py: 1.25,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          gap: 1,
        }}
      >
        <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
          <Select
            displayEmpty
            value={selectedDriverId}
            onChange={(e) => onDriverChange(e.target.value)}
            sx={{ fontSize: 13, borderRadius: 1.5 }}
          >
            <MenuItem value="" disabled>
              <Box component="em" sx={{ color: "text.secondary" }}>
                Motorista…
              </Box>
            </MenuItem>
            {drivers.map((d) => (
              <MenuItem key={d.driver_id} value={d.driver_id}>
                {d.name}
                {d.has_active_shipping_route ? " · em rota" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={
            !selectedDriverId || dispatching || route.orders.length === 0
          }
          onClick={onDispatch}
          startIcon={
            dispatching ? (
              <CircularProgress size={14} sx={{ color: "inherit" }} />
            ) : (
              <LocalShippingIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            bgcolor: color,
            color: theme.palette.getContrastText(color),
            borderRadius: 1.5,
            textTransform: "none",
            fontWeight: 600,
            whiteSpace: "nowrap",
            "&:hover": {
              bgcolor: color,
              filter: "brightness(1.08)",
            },
          }}
        >
          Despachar
        </Button>
      </CardActions>
    </Card>
  );
}

/* ────── order chip (inside a route card) ────── */

const SPECIAL_RANKING = 3;

function OrderChip({ order, index, color, onDragStart, theme }) {
  const isSpecial = order.ranking_by_total === SPECIAL_RANKING;
  const urgency = getDeadlineState(order.delivery_expected_ts).state;
  const orderTint =
    urgency === "late"
      ? theme.palette.error.main
      : urgency === "almostLate"
      ? theme.palette.warning.main
      : null;
  return (
    <Stack
      draggable
      direction="row"
      spacing={1}
      alignItems="center"
      onDragStart={onDragStart}
      sx={{
        px: 1,
        py: 0.6,
        borderRadius: 1.5,
        cursor: "grab",
        transition: "background 0.15s ease",
        bgcolor: orderTint ? alpha(orderTint, 0.08) : "transparent",
        border: `1px solid ${
          orderTint ? alpha(orderTint, 0.3) : "transparent"
        }`,
        "&:active": { cursor: "grabbing" },
        "&:hover": {
          bgcolor: orderTint
            ? alpha(orderTint, 0.12)
            : alpha(theme.palette.action.hover, 0.5),
        },
      }}
    >
      <DragIndicatorIcon
        sx={{ fontSize: 14, color: theme.palette.action.disabled }}
      />
      <Chip
        label={index}
        size="small"
        sx={{
          height: 20,
          minWidth: 24,
          fontSize: "0.7rem",
          fontWeight: 700,
          bgcolor: alpha(color, 0.12),
          color: color,
          borderRadius: 1,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {isSpecial && <SpecialOrderBadge />}
          <Typography variant="body2" noWrap sx={{ fontSize: 13, minWidth: 0 }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              #{order.order_index}
            </Box>
            {order.customer_name && (
              <Box
                component="span"
                sx={{ color: "text.secondary", fontWeight: 400 }}
              >
                {" · "}
                {order.customer_name}
              </Box>
            )}
          </Typography>
        </Stack>
        {(order.street || order.number) && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: "block", fontSize: "0.7rem" }}
          >
            {order.street} {order.number}
          </Typography>
        )}
      </Box>
      {order.delivery_expected_ts && (
        <DeadlineChip
          deadlineTs={order.delivery_expected_ts}
          theme={theme}
          compact
        />
      )}
    </Stack>
  );
}

/* ────── solo card ────── */

function SoloCard({ order, drivers, onDragStart, onDispatch, dispatching, theme }) {
  const [driverId, setDriverId] = useState("");
  // Card stays neutral white-with-shadow; the deadline color is applied
  // to the order content (avatar + chip) instead.
  const urgency = getDeadlineState(order.delivery_expected_ts).state;
  const orderAccent =
    urgency === "late"
      ? theme.palette.error.main
      : urgency === "almostLate"
      ? theme.palette.warning.main
      : order.urgent
      ? theme.palette.warning.main
      : theme.palette.text.secondary;

  return (
    <Card
      draggable
      onDragStart={onDragStart}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        cursor: "grab",
        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}, 0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
        transition: "all 0.2s ease-in-out",
        "&:active": { cursor: "grabbing" },
        "&:hover": {
          borderColor: alpha(theme.palette.grey[400], 0.8),
          boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.1)}, 0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2, pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Avatar
            sx={{
              bgcolor: alpha(orderAccent, 0.12),
              color: orderAccent,
              width: 32,
              height: 32,
            }}
          >
            {order.urgent ? (
              <WarningAmberIcon fontSize="small" />
            ) : (
              <HourglassEmptyIcon fontSize="small" />
            )}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ mb: 0.25 }}
              flexWrap="wrap"
              rowGap={0.5}
            >
              <Typography variant="subtitle1" fontWeight={600} lineHeight={1}>
                #{order.order_index}
              </Typography>
              {order.ranking_by_total === SPECIAL_RANKING && <SpecialOrderBadge />}
              {order.urgent && (
                <Chip
                  label="urgente"
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.error.main, 0.12),
                    color: theme.palette.error.main,
                    textTransform: "uppercase",
                  }}
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block" }}
            >
              {order.customer_name || "Cliente"}
            </Typography>
          </Box>
          {order.delivery_expected_ts && (
            <DeadlineChip
              deadlineTs={order.delivery_expected_ts}
              theme={theme}
            />
          )}
        </Stack>

        {(order.street || order.number) && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{
              display: "block",
              fontFamily: "monospace",
              fontSize: "0.72rem",
              mt: 0.5,
            }}
          >
            {order.street} {order.number}
            {order.neighborhood && ` · ${order.neighborhood}`}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 1 }}
        >
          <AccessTimeIcon
            sx={{ fontSize: 13, color: theme.palette.text.secondary }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            {order.waiting_minutes?.toFixed(1)} min de espera
          </Typography>
        </Stack>
      </CardContent>

      <CardActions
        onClick={(e) => e.stopPropagation()}
        sx={{
          px: 2,
          py: 1.25,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          gap: 1,
        }}
      >
        <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
          <Select
            displayEmpty
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
            sx={{ fontSize: 13, borderRadius: 1.5 }}
          >
            <MenuItem value="" disabled>
              <Box component="em" sx={{ color: "text.secondary" }}>
                Motorista…
              </Box>
            </MenuItem>
            {drivers.map((d) => (
              <MenuItem key={d.driver_id} value={d.driver_id}>
                {d.name}
                {d.has_active_shipping_route ? " · em rota" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          disabled={!driverId || dispatching}
          onClick={() => onDispatch(driverId)}
          startIcon={
            dispatching ? (
              <CircularProgress size={14} sx={{ color: "inherit" }} />
            ) : (
              <LocalShippingIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            borderColor: orderAccent,
            color: orderAccent,
            borderRadius: 1.5,
            textTransform: "none",
            fontWeight: 600,
            whiteSpace: "nowrap",
            "&:hover": {
              borderColor: orderAccent,
              bgcolor: alpha(orderAccent, 0.08),
            },
          }}
        >
          Sozinho
        </Button>
      </CardActions>
    </Card>
  );
}

/* ────── deadline chip (route header + solo header + chip-compact in order rows) ────── */

// Match the app's existing late/almost-late convention from ordersSlice.js:
//   lateStatus  = delivery_expected_time < now
//   isAlmostLate = delivery_expected_time within next 10min
// Anything else is on-time (neutral).
const ALMOST_LATE_MINUTES = 10;

function getDeadlineState(deadlineTs) {
  if (!deadlineTs) return { state: "none", slackMin: Infinity };
  const slackMin = (deadlineTs * 1000 - Date.now()) / 60000;
  if (slackMin < 0) return { state: "late", slackMin };
  if (slackMin < ALMOST_LATE_MINUTES) return { state: "almostLate", slackMin };
  return { state: "onTime", slackMin };
}

// Worst-case severity across a route's stops — any single late order makes
// the whole route late, any almost-late order makes it almost late.
function getRouteSeverity(route) {
  let worst = "none";
  const rank = { late: 3, almostLate: 2, onTime: 1, none: 0 };
  for (const o of route.orders || []) {
    const s = getDeadlineState(o.delivery_expected_ts).state;
    if (rank[s] > rank[worst]) worst = s;
  }
  return worst;
}

function DeadlineChip({ deadlineTs, theme, compact = false }) {
  const { state, slackMin } = getDeadlineState(deadlineTs);

  const palette = {
    late: theme.palette.error.main,
    almostLate: theme.palette.warning.main,
    onTime: theme.palette.text.secondary,
  };
  const color = palette[state];
  // On-time stays neutral (no tinted background) so it doesn't compete
  // visually with the genuinely-urgent ones.
  const bg =
    state === "onTime"
      ? alpha(theme.palette.text.primary, 0.06)
      : alpha(color, 0.14);

  const date = new Date(deadlineTs * 1000);
  const hhmm = date.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const slackLabel =
    state === "late"
      ? `${Math.round(Math.abs(slackMin))}min atrasado`
      : `em ${Math.round(slackMin)}min`;

  if (compact) {
    return (
      <Chip
        icon={<AccessTimeIcon sx={{ fontSize: 11 }} />}
        label={hhmm}
        size="small"
        sx={{
          height: 18,
          fontSize: "0.65rem",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          bgcolor: bg,
          color,
          "& .MuiChip-icon": { color, fontSize: 11, ml: "4px" },
        }}
      />
    );
  }

  return (
    <Tooltip title={slackLabel} placement="top" arrow>
      <Chip
        icon={<AccessTimeIcon sx={{ fontSize: 13 }} />}
        label={hhmm}
        size="small"
        sx={{
          height: 22,
          fontSize: "0.72rem",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          bgcolor: bg,
          color,
          borderRadius: 1,
          "& .MuiChip-icon": { color, fontSize: 13 },
        }}
      />
    </Tooltip>
  );
}

/* ────── dispatch-sequence indicator ────── */

function DispatchSequenceChip({ index, theme }) {
  if (index === 0) {
    return (
      <Chip
        icon={<FlashOnIcon sx={{ fontSize: 12 }} />}
        label="Próxima"
        size="small"
        sx={{
          height: 18,
          fontSize: "0.62rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          bgcolor: alpha(theme.palette.primary.main, 0.14),
          color: theme.palette.primary.main,
          borderRadius: 1,
          "& .MuiChip-icon": {
            color: theme.palette.primary.main,
            fontSize: 12,
            ml: "4px",
          },
        }}
      />
    );
  }

  // 2ª, 3ª, 4ª… for subsequent dispatch order
  return (
    <Tooltip title={`${index + 1}ª rota a despachar`} placement="top" arrow>
      <Chip
        label={`${index + 1}ª`}
        size="small"
        sx={{
          height: 18,
          minWidth: 24,
          fontSize: "0.65rem",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          bgcolor: alpha(theme.palette.text.primary, 0.07),
          color: theme.palette.text.secondary,
          borderRadius: 1,
        }}
      />
    </Tooltip>
  );
}

/* ────── golden crown for SPECIAL ranking orders ────── */

function SpecialOrderBadge() {
  return (
    <Tooltip title="Pedido especial (alto valor)" placement="top" arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#FFD700",
          color: "#B8860B",
          borderRadius: "50%",
          width: 16,
          height: 16,
          flexShrink: 0,
          boxShadow: "0 1px 3px rgba(184, 134, 11, 0.35)",
        }}
      >
        <WorkspacePremiumIcon sx={{ fontSize: 11 }} />
      </Box>
    </Tooltip>
  );
}
